import React, { useState, useEffect } from "react";
import MTextArea from "@/components/ui/atoms/MTextArea";
import { useProfileField } from "@/hooks/useHealthProfile";
import { getMemberInfo } from "@/hooks/useMembers";
import { useToast } from "@/components/ui/atoms/MToastProvider";
import { MButton } from "@/components/ui/atoms/MButtons";

import MHeader from "@/components/ui/organisms/MHeader";
import { MedicalDetailType } from "@/types/health-profile";
import { useNavigate } from "react-router-dom";
import MSpacer from "@/components/ui/atoms/MSpacer";
import { useTranslation } from "react-i18next";
import { trackUserDataLoaded } from "@/utils/gtm";
import {
  ANALYTICS_SCREEN_TYPES,
  ANALYTICS_SCREEN_NAMES,
} from "@/constants/analytics.constants";
import { useNavigationSource } from "@/hooks/useNavigationSource";

const Notes: React.FC = () => {
  const { t } = useTranslation();
  const { showToast } = useToast();
  const navigate = useNavigate();
  const memberInfo = getMemberInfo();
  const memberId = memberInfo.id;

  // Navigation source
  const { navigateBackIfExists } = useNavigationSource();
  const {
    fieldValue: notes,
    setFieldValue: setNotes,
    saveField: saveNotes,
    isChanged,
    loading,
  } = useProfileField<string>(memberId, MedicalDetailType.NOTE, "");

  const [saving, setSaving] = useState(false);

  // Track page load once
  useEffect(() => {
    trackUserDataLoaded({
      memberInfo,
      screenType: ANALYTICS_SCREEN_TYPES.PROFILE,
      screenName: ANALYTICS_SCREEN_NAMES.EMERGENCY_NOTE_ADD,
    });
  }, []);

  const handleSave = async () => {
    setSaving(true);
    try {
      await saveNotes(notes);

      // Track completion
      trackUserDataLoaded({
        memberInfo,
        screenType: ANALYTICS_SCREEN_TYPES.PROFILE,
        screenName: ANALYTICS_SCREEN_NAMES.EMERGENCY_NOTE_ADD_COMPLETE,
      });

      // Navigate back to source if exists, otherwise go to health profile
      const didNavigate = navigateBackIfExists();
      if (!didNavigate) {
        navigate("/profile/health-profile");
      }
    } catch (error) {
      showToast({
        message: t("errors.toast"),
        variant: "error",
      });
    } finally {
      setSaving(false);
    }
  };

  const isUnchanged = !isChanged();

  return (
    <div
      className="profile-page-root flex flex-col"
      style={{ minHeight: "calc(var(--vh, 1vh) * 100 - 128px)" }}
    >
      <MHeader module="emergency_note" memberInfo={memberInfo} />
      <MSpacer mt={24} />
      <form className="flex-1">
        <div>
          <MTextArea
            className="profile-input-textarea w-full text-left min-h-[203px] resize-none"
            placeholder={t("emergency_note.placeholder")}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            disabled={loading || saving}
            maxLength={1000}
            disableAutoScrollIntoView
          />
        </div>
        <div className="confirm-footer">
          <MButton
            type="button"
            variant="filled"
            size="lg"
            onClick={handleSave}
            disabled={saving || loading || isUnchanged}
            className="w-full"
            text={t("common.done")}
          />
        </div>
      </form>
    </div>
  );
};

export default Notes;
